/**
 * ShapeRenderer - Renders shape compositions to SVG.
 */

import type {
  ShapeComposition,
  ComputedOpening,
  ComputedDivider,
  RenderOptions,
  RenderResult,
  OpeningStyle,
  AssetReference,
  AssetLabel,
} from '../types';
import { LayoutEngine, DEFAULT_RENDER_OPTIONS } from './LayoutEngine';
import { AssetManager } from '../models/AssetManager';
import { UNKNOWN_ASSET_SVG } from '../models/UnknownAsset';

/**
 * Opening/handing label size, in the drawing's own units.
 *
 * The whole drawing is ONE unit: every line and character is sized in drawing
 * coordinates, so scaling it scales all of it together and the same SVG reads
 * identically in the editor, the report card and the full-screen view. Sizing a
 * label off its own opening instead made the same label a different size in
 * every cell, and screen-pixel sizing made it a different size in every view.
 */
const LABEL_FONT_PX = 22;

/**
 * Weight of every structural line - outer frame, mullions, transoms - in the
 * drawing's own units, matching the glyph stroke-width so structure and
 * operation lines read as one drawing. Scales with the drawing like everything
 * else; see LABEL_FONT_PX.
 */
export const LINE_WEIGHT_PX = 4;

/**
 * Dimension text size, in the drawing's own units.
 *
 * The finished drawing is scaled down to fit whatever card or dialog shows it,
 * so this has to be generous enough to survive that reduction - at 11px the
 * dimensions were legible in the editor but not in the report's preview card.
 * It lives in the 40-unit margin the dimension tiers occupy, which bounds how
 * large it can go.
 */
const DIMENSION_FONT_PX = 18;

/** Read a glyph's viewBox so authored label coordinates can be mapped from it. */
export function parseViewBox(
  svg: string
): { width: number; height: number } | null {
  const m = /viewBox="\s*([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)/.exec(
    svg
  );
  if (!m) return null;
  return { width: Number(m[3]), height: Number(m[4]) };
}

/**
 * Map a label from the glyph's authored coordinates onto the box the glyph is
 * drawn in.
 *
 * The POSITION scales with the opening, so a label keeps its designed spot even
 * when the opening is a different shape. The SIZE does not: it is one value in
 * drawing units, both because scaling text by two different factors is the
 * distortion we are avoiding, and because the same label must be the same size
 * in every opening of the drawing.
 *
 * Shared by the canvas and the asset-browser preview so both place labels
 * identically; the preview passes a larger size because a ~56px tile cannot
 * show drawing-scale text.
 */
export function mapLabelToBox(
  label: AssetLabel,
  viewBox: { width: number; height: number },
  box: { x: number; y: number; width: number; height: number },
  fontSize: number = LABEL_FONT_PX
): { x: number; y: number; fontSize: number; textAnchor: string } {
  return {
    x: box.x + (label.x / viewBox.width) * box.width,
    y: box.y + (label.y / viewBox.height) * box.height,
    fontSize,
    textAnchor: label.anchor ?? 'start',
  };
}

/**
 * Line height of a stacked label column, as a multiple of the label's font size.
 * Fixed in drawing units exactly like the size itself, so the space between two
 * lines is the same in every opening of every drawing.
 */
export const LABEL_LINE_HEIGHT = 1.15;

/**
 * The opening/handing label face: the monospace stack the dimension text already
 * uses, so every label character is one width and an OL column lines up with an
 * OR column beside it (a proportional face made them visibly uneven). Single
 * quotes inside, so the one string can sit in a CSS rule and in an SVG attribute.
 * The canvas's .ptc-label rule and the editor's asset tiles both read this.
 */
export const LABEL_FONT_FAMILY =
  "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace";

/** A label placed in a box: its text plus where and how large it is drawn. */
export interface PlacedLabel {
  text: string;
  x: number;
  y: number;
  fontSize: number;
  textAnchor: string;
}

/**
 * Place an asset's labels in the box its glyph is drawn in, stacking any column
 * of labels by line height.
 *
 * Labels sharing one x and one anchor form a column (the opening-direction set's
 * two-line labels: OL over OI, R over ET). The column's LAST line, the one with
 * the greatest authored y and so nearest the opening's bottom edge, is placed by
 * mapLabelToBox exactly as a single label is. Every line above it is placed one
 * line height (fontSize * LABEL_LINE_HEIGHT) above the line below it.
 *
 * Why the upper lines no longer follow their authored y: mapLabelToBox scales a
 * label's POSITION with the opening but keeps its SIZE fixed, so an authored gap
 * between two lines (y 129 over y 146) shrinks with a short opening until the
 * lines touch, and grows with a tall one. Stacking by line height keeps the gap a
 * property of the font. The upper line's authored y stays in the manifest as the
 * design intent (the preview page and the manifest tests read it); the renderer
 * no longer positions by it. Single labels and columns of one are unchanged.
 *
 * The canvas, viewers and report card (renderAssetLabels) and the editor's asset
 * tiles both call this, so the two cannot drift. Returned in the input order.
 */
export function layoutLabelColumn(
  labels: AssetLabel[],
  viewBox: { width: number; height: number },
  box: { x: number; y: number; width: number; height: number },
  fontSize: number = LABEL_FONT_PX
): PlacedLabel[] {
  const placed: PlacedLabel[] = labels.map((label) => ({
    text: label.text,
    ...mapLabelToBox(label, viewBox, box, fontSize),
  }));
  const columns = new Map<string, number[]>();
  labels.forEach((label, i) => {
    const key = `${label.x}|${label.anchor ?? 'start'}`;
    columns.set(key, [...(columns.get(key) ?? []), i]);
  });
  const lineHeight = fontSize * LABEL_LINE_HEIGHT;
  for (const members of columns.values()) {
    if (members.length < 2) continue;
    // Bottom line first: it keeps its scaled authored position, and each line
    // above hangs one line height over the one below it.
    const upward = [...members].sort((a, b) => labels[b].y - labels[a].y);
    for (let k = 1; k < upward.length; k++) {
      placed[upward[k]].y = placed[upward[k - 1]].y - lineHeight;
    }
  }
  return placed;
}

/**
 * Renders shape compositions to SVG strings.
 */
export class ShapeRenderer {
  private layoutEngine: LayoutEngine;
  private options: Required<RenderOptions>;
  private assetManager: AssetManager | null = null;

  constructor(options: RenderOptions = {}) {
    this.options = { ...DEFAULT_RENDER_OPTIONS, ...options };
    this.layoutEngine = new LayoutEngine(this.options);
  }

  /**
   * Set the asset manager for loading SVG assets.
   */
  setAssetManager(manager: AssetManager): void {
    this.assetManager = manager;
  }

  /**
   * Update render options.
   */
  setOptions(options: Partial<RenderOptions>): void {
    this.options = { ...this.options, ...options };
    this.layoutEngine = new LayoutEngine(this.options);
  }

  /**
   * Render a shape composition to SVG.
   */
  async render(
    composition: ShapeComposition,
    options?: Partial<RenderOptions>
  ): Promise<RenderResult> {
    if (options) {
      this.setOptions(options);
    }

    const computedRoot = this.layoutEngine.computeLayout(composition);
    const dimensions = this.layoutEngine.getTotalDimensions(composition);

    // Resolve assets for terminal openings
    await this.resolveAssets(computedRoot);

    // Build SVG string
    const svg = this.buildSVG(composition, computedRoot, dimensions);

    return {
      svg,
      width: dimensions.width,
      height: dimensions.height,
      computedRoot,
      computedRegions: computedRoot, // backwards compat
    };
  }

  /**
   * Render synchronously (only works if all assets are already cached).
   */
  renderSync(composition: ShapeComposition): RenderResult {
    const computedRoot = this.layoutEngine.computeLayout(composition);
    const dimensions = this.layoutEngine.getTotalDimensions(composition);

    // Try to resolve cached assets
    this.resolveCachedAssets(computedRoot);

    const svg = this.buildSVG(composition, computedRoot, dimensions);

    return {
      svg,
      width: dimensions.width,
      height: dimensions.height,
      computedRoot,
      computedRegions: computedRoot,
    };
  }

  /**
   * Resolve assets for all terminal openings.
   *
   * resolvedAssets holds exactly one entry per applied reference, in the same order,
   * because renderOpening and renderAssetLabels pair assets[i] with resolvedAssets[i].
   * Until 2026-09-30 a reference that failed was dropped, which shifted every later
   * glyph onto the wrong reference's labels. An id the manifest does not hold now
   * resolves to the question-mark glyph inside loadAsset with no warning (rule 12,
   * D-150); a fetch that fails for an id the manifest does hold is a broken deployment,
   * so it still warns, and its slot draws the question mark too rather than going empty.
   */
  private async resolveAssets(computed: ComputedOpening): Promise<void> {
    const assetsToLoad =
      computed.opening.assets ??
      (computed.opening.asset ? [computed.opening.asset] : []);

    if (assetsToLoad.length > 0 && this.assetManager) {
      computed.resolvedAssets = [];
      for (const assetRef of assetsToLoad) {
        try {
          computed.resolvedAssets.push(
            await this.assetManager.loadAsset(assetRef)
          );
        } catch (e) {
          console.warn(
            `Failed to load asset: ${assetRef.category}/${assetRef.name}`,
            e
          );
          computed.resolvedAssets.push(UNKNOWN_ASSET_SVG);
        }
      }
      // Backwards compat
      computed.resolvedAsset = computed.resolvedAssets[0];
    }

    if (computed.children) {
      await Promise.all(computed.children.map((c) => this.resolveAssets(c)));
    }
  }

  /**
   * Resolve cached assets synchronously.
   *
   * One entry per applied reference, as in resolveAssets. An id the manifest does not
   * hold comes back from getCached as the question-mark glyph; a known asset not yet
   * fetched holds its slot with an empty string, which draws nothing, so the glyphs
   * after it keep their own labels until the async render fills it in.
   */
  private resolveCachedAssets(computed: ComputedOpening): void {
    const assetsToLoad =
      computed.opening.assets ??
      (computed.opening.asset ? [computed.opening.asset] : []);

    if (assetsToLoad.length > 0 && this.assetManager) {
      computed.resolvedAssets = [];
      for (const assetRef of assetsToLoad) {
        computed.resolvedAssets.push(
          this.assetManager.getCached(assetRef) ?? ''
        );
      }
      // Backwards compat
      computed.resolvedAsset = computed.resolvedAssets[0];
    }

    if (computed.children) {
      computed.children.forEach((c) => this.resolveCachedAssets(c));
    }
  }

  /**
   * Collect test candidate metadata from the computed tree.
   * Returns the test candidate opening label, its asset type, and all distinct asset types.
   */
  private collectTestMetadata(computed: ComputedOpening): {
    testCandidateLabel: string | null;
    testCandidateAssetType: string | null;
    allAssetTypes: string[];
  } {
    const assetTypes = new Set<string>();
    let testCandidateLabel: string | null = null;
    let testCandidateAssetType: string | null = null;

    const traverse = (node: ComputedOpening): void => {
      // Collect asset types from this node
      const assets =
        node.opening.assets ?? (node.opening.asset ? [node.opening.asset] : []);
      for (const asset of assets) {
        if (asset.category) {
          assetTypes.add(asset.category);
        }
      }

      // Check if this is the test candidate
      if (node.opening.testCandidate) {
        testCandidateLabel = node.opening.label ?? node.opening.id ?? null;
        // Get the first asset's category as the test candidate asset type
        if (assets.length > 0 && assets[0].category) {
          testCandidateAssetType = assets[0].category;
        }
      }

      // Recurse into children
      if (node.children) {
        node.children.forEach(traverse);
      }
    };

    traverse(computed);

    return {
      testCandidateLabel,
      testCandidateAssetType,
      allAssetTypes: Array.from(assetTypes).sort(),
    };
  }

  /**
   * Build the complete SVG string.
   */
  private buildSVG(
    composition: ShapeComposition,
    computed: ComputedOpening,
    dimensions: { width: number; height: number }
  ): string {
    const { width, height } = dimensions;
    const defaultStyle =
      composition.defaults?.openingStyle ?? composition.defaults?.regionStyle;

    // Frame styling. Weight is the fixed schematic constant, not authorable.
    const frameColor = composition.defaults?.frameColor ?? '#16324a';

    const parts: string[] = [
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">`,
      `  <defs>`,
      `    <style>`,
      `      .ptc-opening { fill: none; stroke: #333; stroke-width: 1; }`,
      `      .ptc-opening-fill { fill: #f5f5f5; stroke: none; }`,
      // No font-size here: it is set per label as an attribute, scaled to the
      // opening (see computeLabelLayout). A CSS rule would override that.
      `      .ptc-label { font-family: ${LABEL_FONT_FAMILY}; font-weight: 600; fill: #1f3d5c; }`,
      `      .ptc-dimension { font-family: ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace; font-size: ${DIMENSION_FONT_PX}px; fill: #666; font-variant-numeric: tabular-nums; }`,
      `      .ptc-dim-line { stroke: #9ca3af; stroke-width: 1; fill: none; }`,
      `      .ptc-dim-line.locked { stroke: #c2410c; stroke-width: 1.25; }`,
      `      .ptc-dim-witness { stroke: #cbd5e1; stroke-width: 1; }`,
      `      .ptc-dim-arrow { fill: #9ca3af; stroke: none; }`,
      `      .ptc-dim-arrow.locked { fill: #c2410c; }`,
      `      .ptc-dim-text { font-family: ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace; font-size: ${DIMENSION_FONT_PX}px; fill: #6b7280; font-variant-numeric: tabular-nums; paint-order: stroke; stroke: #ffffff; stroke-width: 4px; stroke-linejoin: round; }`,
      `      .ptc-dim-text.locked { fill: #c2410c; font-weight: 600; }`,
      `      .ptc-dim-lock rect { fill: #c2410c; stroke: none; }`,
      `      .ptc-dim-lock path { fill: none; stroke: #c2410c; stroke-width: 1.1; }`,
      `      .ptc-divider { fill: #6b7280; stroke: #374151; stroke-width: 1; }`,
      `      .ptc-mullion { }`,
      `      .ptc-transom { }`,
      `      .ptc-divider-hit { fill: rgba(0,0,0,0.001); pointer-events: all; cursor: pointer; }`,
      `      .ptc-divider-hit[data-divider-direction="horizontal"] { cursor: ew-resize; }`,
      `      .ptc-divider-hit[data-divider-direction="vertical"] { cursor: ns-resize; }`,
      `      .ptc-test-candidate-overlay { fill: #84cc16; fill-opacity: 0.15; stroke: #65a30d; stroke-width: 2; stroke-dasharray: 6 3; pointer-events: none; }`,
      `    </style>`,
      `  </defs>`,
    ];

    // Background
    if (this.options.backgroundColor) {
      parts.push(
        `  <rect x="0" y="0" width="${width}" height="${height}" fill="${this.options.backgroundColor}" />`
      );
    }

    // Outer frame (4 rectangles forming the border)
    // This is rendered BEFORE openings so openings appear on top
    const padding = this.options.padding;
    const frameOuterX = padding;
    const frameOuterY = padding;
    const frameOuterW = width - padding * 2;
    const frameOuterH = height - padding * 2;

    // The outer frame is a single stroked rectangle at the SAME weight as the
    // glyph strokes, and non-scaling like them, so frame, mullions and
    // operation lines all read as one line drawing at any zoom. Drawing it as
    // mm-sized filled bands instead made the frame thinner than the glyphs at
    // one zoom and thicker at another.
    parts.push(
      `  <rect x="${frameOuterX}" y="${frameOuterY}" width="${frameOuterW}" height="${frameOuterH}"` +
        ` fill="none" stroke="${frameColor}" stroke-width="${LINE_WEIGHT_PX}" class="ptc-frame" />`
    );

    // Render openings
    parts.push(this.renderOpening(computed, defaultStyle));

    // Dimension labels (if enabled)
    if (this.options.showDimensions) {
      parts.push(this.renderDimensions(composition, computed));
    }

    // Test candidate metadata (hidden element for programmatic access)
    const testMeta = this.collectTestMetadata(computed);
    if (testMeta.testCandidateLabel || testMeta.allAssetTypes.length > 0) {
      parts.push(`  <metadata>`);
      parts.push(`    <ptc:testConfig xmlns:ptc="https://agwa.com.au/ptc"`);
      if (testMeta.testCandidateLabel) {
        parts.push(
          `      ptc:testCandidateOpening="${this.escapeXml(testMeta.testCandidateLabel)}"`
        );
      }
      if (testMeta.testCandidateAssetType) {
        parts.push(
          `      ptc:testCandidateAssetType="${this.escapeXml(testMeta.testCandidateAssetType)}"`
        );
      }
      if (testMeta.allAssetTypes.length > 0) {
        parts.push(
          `      ptc:allAssetTypes="${testMeta.allAssetTypes.map((t) => this.escapeXml(t)).join(',')}"`
        );
      }
      parts.push(`    />`);
      parts.push(`  </metadata>`);
    }

    parts.push(`</svg>`);

    return parts.join('\n');
  }

  /**
   * Render a single opening and its children.
   */
  private renderOpening(
    computed: ComputedOpening,
    defaultStyle?: OpeningStyle
  ): string {
    const { opening, bounds, children, resolvedAsset, isTerminal, depth } =
      computed;
    const parts: string[] = [];

    // Get effective style
    const style = { ...defaultStyle, ...opening.style };

    // Check for assets
    const assets = opening.assets ?? (opening.asset ? [opening.asset] : []);
    const resolvedAssets =
      computed.resolvedAssets ?? (resolvedAsset ? [resolvedAsset] : []);
    // A slot held by an empty string (a known glyph not yet fetched, in the sync render)
    // draws nothing, so it does not count: an opening with nothing drawable keeps its
    // boundary rect, as it did when such a slot was dropped.
    const hasAssets =
      resolvedAssets.some((svg) => svg.length > 0) &&
      isTerminal &&
      assets.length > 0;

    // Render opening background/boundary if no asset is present
    // (assets have their own borders that should integrate seamlessly)
    if ((this.options.showBoundaries || isTerminal) && !hasAssets) {
      const fill = style.fillColor ?? '#f5f5f5';
      const fillOpacity = style.fillOpacity ?? (isTerminal ? 0.3 : 0.1);
      const stroke = style.strokeColor ?? '#333333';
      const strokeWidth = style.strokeWidth ?? (isTerminal ? 2 : 1);

      parts.push(
        `  <rect`,
        `    x="${bounds.x}" y="${bounds.y}"`,
        `    width="${bounds.width}" height="${bounds.height}"`,
        `    fill="${fill}" fill-opacity="${fillOpacity}"`,
        `    stroke="${stroke}" stroke-width="${strokeWidth}"`,
        `    data-opening-id="${opening.id}"`,
        `    data-opening-path="${computed.path.join('/')}"`,
        `    data-opening-depth="${depth}"`,
        `    data-opening-terminal="${isTerminal}"`,
        `  />`
      );
    }

    // Render all assets (stacked/overlaid)
    if (hasAssets) {
      for (let i = 0; i < resolvedAssets.length; i++) {
        const assetSvg = resolvedAssets[i];
        const assetRef = assets[i];
        if (assetSvg && assetRef) {
          parts.push(this.renderAsset(assetSvg, bounds, assetRef));
        }
      }

      // Add a transparent hit area on top of assets for click detection
      // This ensures clicking on an asset selects this opening, not the root
      parts.push(
        `  <rect`,
        `    x="${bounds.x}" y="${bounds.y}"`,
        `    width="${bounds.width}" height="${bounds.height}"`,
        `    fill="transparent"`,
        `    stroke="none"`,
        `    pointer-events="all"`,
        `    data-opening-id="${opening.id}"`,
        `    data-opening-path="${computed.path.join('/')}"`,
        `    data-opening-depth="${depth}"`,
        `    data-opening-terminal="${isTerminal}"`,
        `  />`
      );
    }

    // Render test candidate overlay if this is the test candidate
    if (opening.testCandidate) {
      parts.push(this.renderTestCandidateOverlay(bounds));
    }

    // Draw the applied asset's labels, positioned where the drawing authored
    // them. Terminal openings only - a container box has no operation glyph.
    if (this.options.showLabels && isTerminal) {
      parts.push(...this.renderAssetLabels(assets, resolvedAssets, bounds));
    }

    // Render children FIRST (so dividers are on top for drag)
    if (children) {
      children.forEach((child) => {
        parts.push(this.renderOpening(child, defaultStyle));
      });
    }

    // Render dividers LAST (hit areas need to be on top of child rects)
    if (computed.dividers && computed.dividers.length > 0) {
      for (const divider of computed.dividers) {
        parts.push(this.renderDivider(divider));
      }
    }

    return parts.join('\n');
  }

  /**
   * Render a divider (mullion or transom).
   */
  private renderDivider(divider: ComputedDivider): string {
    const { type, bounds, style, parentOpeningId, index, direction } = divider;
    const stroke = style?.strokeColor ?? '#374151';

    // Minimum hit area size for easier grabbing (12px)
    const minHitSize = 12;
    const hitAreaExpand =
      direction === 'horizontal'
        ? Math.max(0, (minHitSize - bounds.width) / 2)
        : Math.max(0, (minHitSize - bounds.height) / 2);

    const parts: string[] = [];

    // Mullions and transoms are drawn as a single line down the centre of the
    // divider's span, at the shared structural weight - not as a filled band,
    // which rendered heavier than the glyph lines and grew with zoom.
    const isVertical = bounds.height >= bounds.width;
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    parts.push(
      `  <line x1="${isVertical ? cx : bounds.x}" y1="${isVertical ? bounds.y : cy}"` +
        ` x2="${isVertical ? cx : bounds.x + bounds.width}" y2="${isVertical ? bounds.y + bounds.height : cy}"` +
        ` stroke="${stroke}" stroke-width="${LINE_WEIGHT_PX}" />`
    );

    // Render invisible hit area for dragging (always at least minHitSize)
    const hitBounds =
      direction === 'horizontal'
        ? {
            x: bounds.x - hitAreaExpand,
            y: bounds.y,
            width: Math.max(bounds.width + hitAreaExpand * 2, minHitSize),
            height: bounds.height,
          }
        : {
            x: bounds.x,
            y: bounds.y - hitAreaExpand,
            width: bounds.width,
            height: Math.max(bounds.height + hitAreaExpand * 2, minHitSize),
          };

    parts.push(
      `  <rect`,
      `    x="${hitBounds.x}" y="${hitBounds.y}"`,
      `    width="${hitBounds.width}" height="${hitBounds.height}"`,
      `    data-divider-type="${type}"`,
      `    data-divider-parent="${parentOpeningId}"`,
      `    data-divider-index="${index}"`,
      `    data-divider-direction="${direction}"`,
      `    class="ptc-divider-hit"`,
      `  />`
    );

    return parts.join('\n');
  }

  /**
   * Find the opening / handing label for a terminal opening, from the manifest
   * entry of the first applied asset that declares one.
   *
   * The label lives in manifest metadata rather than in the glyph's path data
   * because a glyph is stretched to the opening with preserveAspectRatio="none";
   * text baked into it would distort with the opening's aspect ratio. Drawing it
   * here keeps it upright and unscaled. Assets whose glyph carries no geometry
   * (fixed light, curtain wall, window wall, roof window) are identified by this
   * label alone.
   */
  private renderAssetLabels(
    assets: AssetReference[],
    resolvedAssets: string[],
    bounds: { x: number; y: number; width: number; height: number }
  ): string[] {
    if (!this.assetManager) return [];
    const parts: string[] = [];
    for (let i = 0; i < assets.length; i++) {
      const ref = assets[i];
      const entry = this.assetManager.getAssetEntry(ref.category, ref.name);
      if (!entry?.labels?.length) continue;
      // Coordinates are authored against this asset's own viewBox, which is not
      // the same for every asset - a French Door is twice as wide as a leaf.
      const viewBox = parseViewBox(resolvedAssets[i] ?? '') ?? {
        width: 100,
        height: 152,
      };
      // One shared layout with the editor's tiles: columns stack by line height.
      for (const l of layoutLabelColumn(entry.labels, viewBox, bounds)) {
        parts.push(
          `  <text x="${l.x.toFixed(1)}" y="${l.y.toFixed(1)}" class="ptc-label" font-size="${l.fontSize.toFixed(1)}" text-anchor="${l.textAnchor}">${this.escapeXml(l.text)}</text>`
        );
      }
    }
    return parts;
  }

  /**
   * Render an asset SVG within bounds.
   */
  private renderAsset(
    svg: string,
    bounds: { x: number; y: number; width: number; height: number },
    ref: AssetReference
  ): string {
    // Extract viewBox from the SVG if present
    const viewBoxMatch = svg.match(/viewBox="([^"]+)"/);
    const viewBox = viewBoxMatch
      ? viewBoxMatch[1]
      : `0 0 ${bounds.width} ${bounds.height}`;

    // Extract presentation attributes from the original <svg> element
    const svgTagMatch = svg.match(/<svg[^>]*>/);
    const svgTag = svgTagMatch ? svgTagMatch[0] : '';

    // Presentation attributes to preserve
    const presentationAttrs: string[] = [];
    const attrPatterns = [
      /\bfill="([^"]+)"/,
      /\bstroke="([^"]+)"/,
      /\bstroke-width="([^"]+)"/,
      /\bstroke-linecap="([^"]+)"/,
      /\bstroke-linejoin="([^"]+)"/,
      /\bvector-effect="([^"]+)"/,
    ];

    const assetColor = '#364f6b';

    for (const pattern of attrPatterns) {
      const match = svgTag.match(pattern);
      if (match) {
        let attrName = pattern.source.match(/\\b(\w+(?:-\w+)?)=/)?.[1] ?? '';
        let attrValue = match[1];
        // Convert currentColor to explicit color
        if (attrValue === 'currentColor') {
          attrValue = assetColor;
        }
        presentationAttrs.push(`${attrName}="${attrValue}"`);
      }
    }

    const presentationStr =
      presentationAttrs.length > 0 ? ' ' + presentationAttrs.join(' ') : '';

    // Build transform for rotation/flip
    const transforms: string[] = [];
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;

    if (ref.rotation) {
      transforms.push(`rotate(${ref.rotation} ${centerX} ${centerY})`);
    }
    if (ref.flipX) {
      transforms.push(
        `translate(${bounds.x * 2 + bounds.width} 0) scale(-1 1)`
      );
    }
    if (ref.flipY) {
      transforms.push(
        `translate(0 ${bounds.y * 2 + bounds.height}) scale(1 -1)`
      );
    }

    const transformAttr =
      transforms.length > 0 ? ` transform="${transforms.join(' ')}"` : '';

    // Strip outer SVG tags and wrap content in a group
    const innerContent = svg
      .replace(/<\?xml[^?]*\?>/g, '')
      .replace(/<svg[^>]*>/g, '')
      .replace(/<\/svg>/g, '')
      .trim();

    return [
      `  <g data-asset="${ref.category}/${ref.name}"${transformAttr}>`,
      `    <svg x="${bounds.x}" y="${bounds.y}" width="${bounds.width}" height="${bounds.height}" viewBox="${viewBox}" preserveAspectRatio="none"${presentationStr}>`,
      `      ${innerContent}`,
      `    </svg>`,
      `  </g>`,
    ].join('\n');
  }

  /**
   * Render dimension labels for the composition.
   */
  /**
   * Dimension overlay. Draws the overall width/height in the canvas margin
   * (outer tiers - the editor/thumbnail widen `padding` when dimensions are on
   * so there is room), plus per-column widths (inner bottom tier) and per-row
   * heights (interior, per multi-row column). Locked tracks - read from
   * `opening.sizeLocked`, set by GridModel.applyLock - render in the locked
   * accent with a padlock; auto-equalized tracks render lighter, no padlock.
   *
   * Only the top-level grid is annotated; deeper nested cells are not (would
   * clutter the schematic). Sizes are derived from computed geometry, never
   * stored.
   */
  private renderDimensions(
    composition: ShapeComposition,
    computed: ComputedOpening
  ): string {
    const scale = this.options.scale;
    const pad = this.options.padding;
    const fx = pad;
    const fy = pad;
    const fw = composition.size.width.value * scale;
    const fh = composition.size.height.value * scale;

    const parts: string[] = ['  <g class="ptc-dimensions">'];

    // Overall width - outer bottom tier.
    const wY = fy + fh + 40;
    parts.push(this.dimWitness(fx, fy + fh, fx, wY));
    parts.push(this.dimWitness(fx + fw, fy + fh, fx + fw, wY));
    parts.push(this.dimLineH(fx, fx + fw, wY, false));
    parts.push(
      this.dimText(
        fx + fw / 2,
        wY - 5,
        this.formatDimension(composition.size.width),
        false,
        'middle'
      )
    );

    // Overall height - outer left tier.
    const hX = fx - 40;
    parts.push(this.dimWitness(fx, fy, hX, fy));
    parts.push(this.dimWitness(fx, fy + fh, hX, fy + fh));
    parts.push(this.dimLineV(fy, fy + fh, hX, false));
    parts.push(
      this.dimTextV(
        hX - 5,
        fy + fh / 2,
        this.formatDimension(composition.size.height),
        false
      )
    );

    // Lock indicators (accent + padlock) are an editor authoring aid. Exports
    // and thumbnails turn them off for a plain technical drawing - dimensions
    // only, neutral colour, no padlocks.
    const showLocks = this.options.lockIndicators;

    // Per-column widths (inner bottom tier) and per-row heights (interior).
    if (!computed.isTerminal && computed.children) {
      const colY = fy + fh + 16;
      for (const col of computed.children) {
        const b = col.bounds;
        const colLocked = showLocks && col.opening.sizeLocked === true;
        parts.push(this.dimLineH(b.x, b.x + b.width, colY, colLocked));
        parts.push(
          this.dimLabel(
            b.x + b.width / 2,
            colY - 5,
            `${Math.round(b.width / scale)}`,
            colLocked,
            'middle'
          )
        );

        // Row heights only for multi-row columns (single-row height == overall).
        if (col.children && col.children.length > 1) {
          const rowX = b.x + 12;
          for (const row of col.children) {
            const rb = row.bounds;
            const rowLocked = showLocks && row.opening.sizeLocked === true;
            parts.push(this.dimLineV(rb.y, rb.y + rb.height, rowX, rowLocked));
            parts.push(
              this.dimLabel(
                rowX + 4,
                rb.y + rb.height / 2 + 4,
                `${Math.round(rb.height / scale)}`,
                rowLocked,
                'start'
              )
            );
          }
        }
      }
    }

    parts.push('  </g>');
    return parts.join('\n');
  }

  /** Horizontal dimension line with small inward arrowheads at each end. */
  private dimLineH(x1: number, x2: number, y: number, locked: boolean): string {
    const cls = locked ? 'ptc-dim-line locked' : 'ptc-dim-line';
    const acls = locked ? 'ptc-dim-arrow locked' : 'ptc-dim-arrow';
    const L = 5;
    const h = 2.2;
    return [
      `  <line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" class="${cls}" />`,
      `  <path class="${acls}" d="M ${x1} ${y} L ${x1 + L} ${y - h} L ${x1 + L} ${y + h} Z" />`,
      `  <path class="${acls}" d="M ${x2} ${y} L ${x2 - L} ${y - h} L ${x2 - L} ${y + h} Z" />`,
    ].join('\n');
  }

  /** Vertical dimension line with small inward arrowheads at each end. */
  private dimLineV(y1: number, y2: number, x: number, locked: boolean): string {
    const cls = locked ? 'ptc-dim-line locked' : 'ptc-dim-line';
    const acls = locked ? 'ptc-dim-arrow locked' : 'ptc-dim-arrow';
    const L = 5;
    const h = 2.2;
    return [
      `  <line x1="${x}" y1="${y1}" x2="${x}" y2="${y2}" class="${cls}" />`,
      `  <path class="${acls}" d="M ${x} ${y1} L ${x - h} ${y1 + L} L ${x + h} ${y1 + L} Z" />`,
      `  <path class="${acls}" d="M ${x} ${y2} L ${x - h} ${y2 - L} L ${x + h} ${y2 - L} Z" />`,
    ].join('\n');
  }

  /** Thin extension/witness line from a frame edge to a dimension tier. */
  private dimWitness(x1: number, y1: number, x2: number, y2: number): string {
    return `  <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" class="ptc-dim-witness" />`;
  }

  private dimText(
    x: number,
    y: number,
    text: string,
    locked: boolean,
    anchor: 'start' | 'middle' | 'end'
  ): string {
    const cls = locked ? 'ptc-dim-text locked' : 'ptc-dim-text';
    return `  <text x="${x}" y="${y}" class="${cls}" text-anchor="${anchor}">${this.escapeXml(text)}</text>`;
  }

  /** Height label rotated to read up the left margin. */
  private dimTextV(
    x: number,
    y: number,
    text: string,
    locked: boolean
  ): string {
    const cls = locked ? 'ptc-dim-text locked' : 'ptc-dim-text';
    return `  <text x="${x}" y="${y}" class="${cls}" text-anchor="middle" transform="rotate(-90 ${x} ${y})">${this.escapeXml(text)}</text>`;
  }

  /**
   * Dimension label, with a leading padlock when locked, laid out as one tidy
   * unit so the lock never floats off from its number. 'middle' centres the
   * whole lock+text unit on x; 'start' reads left-to-right from x. y is the
   * text baseline.
   */
  private dimLabel(
    x: number,
    y: number,
    text: string,
    locked: boolean,
    anchor: 'start' | 'middle'
  ): string {
    if (!locked) {
      return this.dimText(x, y, text, false, anchor);
    }
    const lockW = 7;
    const gap = 3;
    const textW = text.length * 6.3; // ~11px sans-serif digit advance
    const startX = anchor === 'middle' ? x - (lockW + gap + textW) / 2 : x;
    return [
      this.lockGlyph(startX, y),
      this.dimText(startX + lockW + gap, y, text, true, 'start'),
    ].join('\n');
  }

  /**
   * Small padlock (~7px wide) whose lower-left sits at (x, baselineY), so it
   * aligns with adjacent text on the same baseline.
   */
  private lockGlyph(x: number, baselineY: number): string {
    const w = 7;
    const bodyH = 5;
    const bodyTop = baselineY - bodyH - 1;
    const r = 2;
    const cx = x + w / 2;
    const shackleTop = bodyTop - r;
    return [
      `  <g class="ptc-dim-lock">`,
      `    <rect x="${x}" y="${bodyTop}" width="${w}" height="${bodyH}" rx="1" />`,
      `    <path d="M ${cx - r} ${bodyTop} V ${shackleTop} A ${r} ${r} 0 0 1 ${cx + r} ${shackleTop} V ${bodyTop}" />`,
      `  </g>`,
    ].join('\n');
  }

  /**
   * Format a dimension for display.
   */
  private formatDimension(dim: { value: number; unit: string }): string {
    return `${dim.value}${dim.unit}`;
  }

  /**
   * Escape XML special characters.
   */
  private escapeXml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  /**
   * Render a test candidate overlay (transparent green rectangle) over the region.
   */
  private renderTestCandidateOverlay(bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): string {
    return [
      `  <rect`,
      `    x="${bounds.x}" y="${bounds.y}"`,
      `    width="${bounds.width}" height="${bounds.height}"`,
      `    class="ptc-test-candidate-overlay"`,
      `    data-test-candidate="true"`,
      `  />`,
    ].join('\n');
  }

  /**
   * Get the layout engine for advanced operations.
   */
  getLayoutEngine(): LayoutEngine {
    return this.layoutEngine;
  }
}

/**
 * Create a shape renderer with default options.
 */
export function createRenderer(options?: RenderOptions): ShapeRenderer {
  return new ShapeRenderer(options);
}
